import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import NonBookableAlert from '@/components/NonBookableAlert.jsx';

import { PVSProvider, buildAvailabilityWindow } from '@/utils/availabilityUtils.js';
import { dayjs } from '@/utils/dayjsSetup.js';
import { JourneyType, fetchAvailableProfessionals, processAPIWithHeadersForPractitioners } from '@/utils/helpers.js';
import { formatInsuranceType } from '@/utils/insurance-utils.ts';

import { useBookingStore, useConfigStore, usePersistedPrismicStore, useSearchStore } from '@/store.js';

import EternoSpinner from '../EternoLogoSpinner/eterno-spinner.tsx';
import FilledButton from '../FilledButton.jsx';
import { StepLabel } from './step-label.tsx';
import { TimeslotDayStrip } from './timeslot-day-strip.tsx';
import { TimeslotPatientType } from './timeslot-patient-type.tsx';
import { TimeslotSlotList } from './timeslot-slot-list.tsx';

// Must match the window `buildAvailabilityWindow` returns, or paging would skip or repeat days.
const AVAILABILITY_WINDOW_DAYS = 5;

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        position: 'relative',
    },
    calendarContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem',
        boxSizing: 'border-box',
        position: 'relative',
        minHeight: '12rem',
    },
    step: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
    },
    question: {
        ...theme.typography.body1,
        fontWeight: theme.weight.heading,
        color: theme.palette.common.darkGrey,
    },
    nextSlotContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: '1rem',
        padding: '2rem 0',
    },
    nextSlotButton: {
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'center',
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        background: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
    },
}));

const TimeslotCalendar = () => {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const [availableTimeSlots, setAvailableTimeSlots] = useState(null);
    const [nextSlot, setNextSlot] = useState(null);
    const [noSlotsAvailable, setNoSlotsAvailable] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const {
        [i18n.language]: { mainData: prismicData },
    } = usePersistedPrismicStore((state) => state.bookAppointmentData);
    const selectedLocation = useSearchStore((state) => state.selectedLocation);
    const selectedTreatments = useSearchStore((state) => state.selectedTreatments);
    const selectedHealthInsurance = useSearchStore((state) => state.selectedHealthInsurance);
    const selectedSpecialty = useSearchStore((state) => state.selectedSpecialty);
    const {
        isOnlineAppointment,
        isReturningPatient,
        selectedProfessional,
        selectedTimeslot,
        calendarStartDate: startDate,
        loading,
        pvsProvider,
        targetId,
        isProfessionalSelectionSkipped,
        invalidTimeSlot,
        professionalDetails,
    } = useBookingStore();

    const updateSelectedSlot = (slot) => {
        if (invalidTimeSlot === slot) return;

        useBookingStore.setState({
            selectedTimeslot: selectedTimeslot !== slot ? slot : null,
        });
    };

    const getProfessional = async () => {
        const response = await fetchAvailableProfessionals(
            selectedLocation,
            selectedSpecialty ?? selectedProfessional.specialityType.key[0].text,
            Object.keys(selectedTreatments)
                .filter((treatmentKey) => selectedTreatments[treatmentKey])
                .join(),
            selectedHealthInsurance.replace('_', '-'),
            JourneyType.SEARCH_BOOK
        );

        if (!response?.data) return null;

        const professional = response.data.find((p) => p.key === selectedProfessional.key[0].text);

        return professional ?? null;
    };

    const getAvailableTimeSlotsAndDetails = async (date) => {
        const insuranceType = formatInsuranceType(selectedHealthInsurance);
        if (!insuranceType) {
            throw new Error(`Invalid insurance type: ${selectedHealthInsurance}`);
        }
        useBookingStore.setState({ loading: true });

        let professional = null;
        if (isProfessionalSelectionSkipped && !targetId && !professionalDetails) {
            professional = await getProfessional();

            if (!professional) {
                useBookingStore.setState({ loading: false });
                setAvailableTimeSlots(undefined);
                setNextSlot(null);
                return;
            }
            useBookingStore.setState({
                targetId: professional.dc_calendar_ids?.[0] ?? professional.data_cur_resource_ids?.[0],
                pvsProvider: professional.pvs_provider,
                professionalDetails: professional,
                loading: false,
            });
            return;
        }

        const targetIdValue =
            targetId ?? professional?.dc_calendar_ids?.[0] ?? professional?.data_cur_resource_ids?.[0];
        const pvsProviderValue = pvsProvider ?? professional?.pvs_provider;
        const professionalId = professionalDetails?.id ?? professional?.id;

        const queryParams = new URLSearchParams({
            is_online: isOnlineAppointment,
            is_returning: isReturningPatient,
            insurance_type: insuranceType,
            appointment_type_keys: Object.keys(selectedTreatments)
                .filter((treatmentKey) => selectedTreatments[treatmentKey])
                .join(),
            start_date: date,
            location_key: selectedLocation,
            journey_type: JourneyType.SEARCH_BOOK,
        });

        if (pvsProviderValue === PVSProvider.DOC_CIRRUS) {
            queryParams.set('dc_calendar_id', targetIdValue);
        } else if (pvsProviderValue === PVSProvider.DATA_CUR) {
            queryParams.set('data_cur_resource_id', targetIdValue);
        }

        const response = await processAPIWithHeadersForPractitioners(
            `public/users/availability/${professionalId}?${queryParams.toString()}`,
            'GET',
            {
                headers: {
                    'Content-Type': 'application/json',
                    customer_id: useConfigStore.getState().currentCustomer?.customer_id?.[0]?.text,
                },
            }
        );

        setSelectedDay(null);
        // The window is being replaced, so a slot chosen against the previous one no longer applies.
        useBookingStore.setState({ selectedTimeslot: null });
        setAvailableTimeSlots(
            response.data?.availability ? buildAvailabilityWindow(response.data.availability, date) : undefined
        );
        setNextSlot(response.data?.next_slot);
        useBookingStore.setState({
            appointmentData: response.data?.appointment,
            loading: false,
        });
    };

    const setPatientType = (isReturning) => {
        if (isReturning === isReturningPatient) return;

        useBookingStore.setState({
            isReturningPatient: isReturning,
            showPatientTypeSelection: false,
            selectedTimeslot: null,
        });
    };

    useEffect(() => {
        if (availableTimeSlots) setAvailableTimeSlots(null);
        getAvailableTimeSlotsAndDetails(startDate);
    }, [isReturningPatient, professionalDetails]);

    const availabilityDays = useMemo(() => availableTimeSlots ?? [], [availableTimeSlots]);

    useEffect(() => {
        if (availableTimeSlots === null) return;

        const hasSlots = availabilityDays.some((day) => day.slots?.length > 0);
        setNoSlotsAvailable(!hasSlots);
    }, [availableTimeSlots]);

    const patientTypeOptions = [
        {
            label: prismicData?.new_patient_button?.[0]?.text,
            isReturning: false,
            disabled: !professionalDetails?.is_for_new_patients,
            disabledHint: prismicData?.infotip_for_new_patient_cta_disabled?.[0]?.text,
        },
        {
            label: prismicData?.current_patient_button?.[0]?.text,
            isReturning: true,
            disabled: !professionalDetails?.is_for_returning_patients,
            disabledHint: prismicData?.infotip_for_returning_patient_cta_disabled?.[0]?.text,
        },
    ];

    const days = useMemo(
        () => availabilityDays.map((day) => ({ date: day.date, count: day.slots?.length ?? 0 })),
        [availabilityDays]
    );

    // Only one day is visible at a time, so a slot picked on another day would stay selected unseen.
    const selectDay = (date) => {
        setSelectedDay(date);
        if (selectedTimeslot && !dayjs(selectedTimeslot).isSame(date, 'day')) {
            useBookingStore.setState({ selectedTimeslot: null });
        }
    };

    const activeDay = selectedDay ?? days.find((day) => day.count > 0)?.date ?? null;
    const activeSlots = availabilityDays.find((day) => day.date === activeDay)?.slots ?? [];

    const jumpToFirstAvailableSlot = async () => {
        const newStartDate = dayjs(nextSlot).format('YYYY-MM-DD');
        setAvailableTimeSlots(null);
        useBookingStore.setState({ calendarStartDate: newStartDate });
        await getAvailableTimeSlotsAndDetails(newStartDate);
    };

    const moveBackward = async () => {
        const newStartDate = dayjs(startDate).subtract(AVAILABILITY_WINDOW_DAYS, 'day').format('YYYY-MM-DD');
        setAvailableTimeSlots(null);
        useBookingStore.setState({ calendarStartDate: newStartDate });
        await getAvailableTimeSlotsAndDetails(newStartDate);
    };

    const moveForward = async () => {
        const newStartDate = dayjs(startDate).add(AVAILABILITY_WINDOW_DAYS, 'day').format('YYYY-MM-DD');
        setAvailableTimeSlots(null);
        useBookingStore.setState({ calendarStartDate: newStartDate });
        await getAvailableTimeSlotsAndDetails(newStartDate);
    };

    const memoizedConditions = useMemo(() => {
        const showControls = !noSlotsAvailable || nextSlot || availableTimeSlots;

        // The jump is only useful when nothing in the shown window can be booked.
        const showNextSlot = noSlotsAvailable && Boolean(availableTimeSlots);

        const showNextSlotButton = Boolean(nextSlot) && !loading;

        const showNoSlots = noSlotsAvailable && !availableTimeSlots && !loading;

        return {
            showControls: showControls,
            showNextSlot: showNextSlot,
            showNextSlotButton: showNextSlotButton,
            showNoSlots: showNoSlots,
        };
    }, [noSlotsAvailable, nextSlot, availableTimeSlots, prismicData, loading]);

    const { showControls, showNextSlot, showNextSlotButton, showNoSlots } = memoizedConditions;

    return (
        <div className={classes.container}>
            {loading && (
                <div className={classes.loadingOverlay}>
                    <EternoSpinner />
                </div>
            )}
            <div className={classes.calendarContainer}>
                {professionalDetails?.is_bookable_by_patient === true && !loading && (
                    <div className={classes.step}>
                        <StepLabel label={t('TimeslotStepOne')} />
                        <Typography className={classes.question}>{t('TimeslotPatientTypeQuestion')}</Typography>
                        <TimeslotPatientType
                            options={patientTypeOptions}
                            isReturning={isReturningPatient}
                            onChange={setPatientType}
                        />
                    </div>
                )}
                {days.length > 0 && (
                    <div className={classes.step}>
                        <StepLabel label={t('TimeslotStepTwo')} />
                        <Typography className={classes.question}>{t('TimeslotSelectionQuestion')}</Typography>
                        <TimeslotDayStrip
                            days={days}
                            selectedDate={activeDay}
                            onSelect={selectDay}
                            onPrevious={moveBackward}
                            onNext={moveForward}
                            showControls={showControls}
                        />
                    </div>
                )}
                {activeSlots.length > 0 && (
                    <TimeslotSlotList
                        slots={activeSlots}
                        selectedSlot={selectedTimeslot}
                        invalidSlot={invalidTimeSlot}
                        onSelect={updateSelectedSlot}
                    />
                )}
                {showNextSlot && (
                    <div className={classes.nextSlotContainer}>
                        <Typography className={classes.nextSlotText}>
                            {noSlotsAvailable && !nextSlot
                                ? prismicData.no_timeslots_for_this_period[0].text
                                : `${prismicData.next_slot_available_text[0].text} ${dayjs(nextSlot).format(
                                      'dddd'
                                  )}, ${dayjs(nextSlot).format(i18n.language === 'en' ? 'MMMM Do' : 'DD. MMMM')}`}
                        </Typography>
                        {showNextSlotButton && (
                            <div className={classes.nextSlotButton}>
                                <FilledButton
                                    text={prismicData.next_slot_cta[0].text}
                                    onPress={jumpToFirstAvailableSlot}
                                    dataTestId="booking-next-slot-btn"
                                />
                            </div>
                        )}
                    </div>
                )}
                {showNoSlots && (
                    <div className={classes.nextSlotContainer} style={{ marginTop: 0 }}>
                        <Typography className={classes.nextSlotText}>
                            {prismicData.no_timeslots_available_text[0].text}
                        </Typography>
                    </div>
                )}
            </div>
            {professionalDetails && professionalDetails.is_bookable_by_patient !== true && !loading && (
                <NonBookableAlert />
            )}
        </div>
    );
};

export default TimeslotCalendar;
